import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Sparkles, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { createLead } from '../lib/api';
import { PROGRAMS } from '../lib/constants';

const EMPTY = {
  name: '',
  phone: '',
  email: '',
  student_age: '',
  program: '',
  preferred_time: '',
  message: '',
};

const LeadModal = ({ open, onOpenChange, source = 'website', defaults = {} }) => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, ...defaults });
      setSuccess(false);
    }
  }, [open, defaults]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Please enter your name and phone number.');
      return;
    }
    setSubmitting(true);
    try {
      await createLead({ ...form, source });
      setSuccess(true);
      toast.success('You\u2019re in. We\u2019ll be in touch shortly.');
    } catch (err) {
      toast.error('Something went wrong. Please call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0B0D10] border border-[color:var(--neon-red)]/35 text-white max-w-lg w-[calc(100vw-1.5rem)] max-h-[92dvh] overflow-y-auto p-5 sm:p-6"
        data-testid="lead-modal"
        style={{ boxShadow: '0 0 0 1px rgba(255,42,42,0.35), 0 30px 80px rgba(0,0,0,0.6)' }}
      >
        {!success ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight text-2xl">
                Claim Your <span className="text-[color:var(--neon-red)]">Free Trial Class</span>
              </DialogTitle>
              <DialogDescription className="text-white/65">
                Tell us a little about yourself — we’ll text you back within hours to set you up.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-1">
                <Label className="text-white/75 text-xs uppercase tracking-[0.14em]">Name *</Label>
                <Input
                  required
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-white/40"
                  placeholder="Your full name"
                  data-testid="lead-input-name"
                />
              </div>
              <div className="sm:col-span-1">
                <Label className="text-white/75 text-xs uppercase tracking-[0.14em]">Phone *</Label>
                <Input
                  required
                  type="tel"
                  value={form.phone}
                  onChange={(e) => set('phone', e.target.value)}
                  className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-white/40"
                  placeholder="(719) 555-0123"
                  data-testid="lead-input-phone"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-white/75 text-xs uppercase tracking-[0.14em]">Email (optional)</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                  className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-white/40"
                  placeholder="you@email.com"
                  data-testid="lead-input-email"
                />
              </div>
              <div className="sm:col-span-1">
                <Label className="text-white/75 text-xs uppercase tracking-[0.14em]">Student Age</Label>
                <Input
                  value={form.student_age}
                  onChange={(e) => set('student_age', e.target.value)}
                  className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-white/40"
                  placeholder="e.g. 7, 10, Adult"
                  data-testid="lead-input-age"
                />
              </div>
              <div className="sm:col-span-1">
                <Label className="text-white/75 text-xs uppercase tracking-[0.14em]">Program</Label>
                <Select value={form.program} onValueChange={(v) => set('program', v)}>
                  <SelectTrigger
                    className="mt-1 bg-white/5 border-white/15 text-white"
                    data-testid="lead-select-program"
                  >
                    <SelectValue placeholder="Choose a program" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B0D10] border-white/15 text-white">
                    {PROGRAMS.map((p) => (
                      <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                    ))}
                    <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-white/75 text-xs uppercase tracking-[0.14em]">Preferred Day / Time</Label>
                <Input
                  value={form.preferred_time}
                  onChange={(e) => set('preferred_time', e.target.value)}
                  className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-white/40"
                  placeholder="e.g. Weekday evenings, Saturday morning"
                  data-testid="lead-input-time"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-white/75 text-xs uppercase tracking-[0.14em]">Anything we should know?</Label>
                <Textarea
                  value={form.message}
                  onChange={(e) => set('message', e.target.value)}
                  className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-white/40 min-h-[72px]"
                  placeholder="Goals, questions, scheduling notes…"
                  data-testid="lead-input-message"
                />
              </div>
              <DialogFooter className="sm:col-span-2 mt-2">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[color:var(--neon-red)] hover:bg-[#ff3a3a] text-white font-display uppercase tracking-[0.14em] rounded-xl py-6 glow-red-md"
                  data-testid="lead-modal-submit-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" /> Book My Free Class
                    </>
                  )}
                </Button>
              </DialogFooter>
              <p className="sm:col-span-2 text-[11px] text-white/45 text-center">
                No spam. No pressure. We’ll text you to confirm.
              </p>
            </form>
          </>
        ) : (
          <div className="py-6 text-center" data-testid="lead-modal-success-message">
            <div className="mx-auto h-14 w-14 rounded-full bg-[color:var(--neon-red)]/15 border border-[color:var(--neon-red)]/45 grid place-items-center glow-red-md">
              <Check className="h-7 w-7 text-[color:var(--neon-red)]" />
            </div>
            <DialogTitle className="mt-5 font-display tracking-tight text-2xl">
              You’re in.
            </DialogTitle>
            <p className="mt-3 text-white/75 max-w-sm mx-auto">
              We’ll text you within hours to confirm your free class at the
              Revolution location. See you on the mat.
            </p>
            <Button
              onClick={() => onOpenChange(false)}
              className="mt-6 bg-white/5 hover:bg-white/10 text-white border border-white/15 font-display uppercase tracking-[0.14em]"
              data-testid="lead-modal-close-button"
            >
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LeadModal;
